/* global ANNOUNCEKIT_WHATSNEW_WIDGET_ID, ANNOUNCEKIT_SUGGEST_FEATURE_WIDGET_ID */
import {
  DownOutlined,
  LogoutOutlined,
  ExclamationCircleOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import AnnounceKit from 'announcekit-react';
import {
  Breadcrumb as AntBreadcrumb,
  Button,
  Dropdown,
  Layout as AntLayout,
  Modal,
  Space,
  Row,
} from 'antd';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, generatePath, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';

import Loading from 'common/components/Loading';
import {
  SIDE_NAV_PROPERTY,
  SIDE_NAV_WIDTH,
  SideNavExpandedContext,
} from 'common/hooks/useSideNavExpanded';
import { getName as getEventName } from 'common/state/event';
import { getName } from 'common/state/organization';
import { getIsStaff, getUser, setUser } from 'common/state/user';

import LogoImage from '@assets/images/em-logos/EM-logo-colour-dark.svg';
import SeparatorImage from '@assets/images/illustrations/breadcrumb-separator.svg';
import AppAlerts from '@components/AppAlerts';
import HelpDropdown from '@components/HelpDropdown';
import UploadingDrawer, {
  useUploadingDrawer,
  STATUS_COMPLETED,
  STATUS_FAILED,
} from '@components/UploadingDrawer';
import { logout } from '@domains/Auth/service';
import PreviewButton from '@domains/Event/Preview';
import useOrganization from '@domains/Organization/useOrganization';
import useOrganizationTabs from '@hooks/useOrganizationTabs';

const { Header: AntHeader, Content: AntContent } = AntLayout;

const PageLayout = styled(AntLayout)`
  height: 100%;
`;

const PageHeaderRoot = styled.div`
  // stop sticky elements at the bottom of the app bar - e.g. sticky table header
  position: relative;
  padding: 0 5px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  flex-wrap: nowrap;
`;

const PageHeader = ({ title, extra }) => (
  <PageHeaderRoot>
    {title}
    {extra}
  </PageHeaderRoot>
);

const Header = styled(AntHeader)`
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  height: auto;
  padding: 0;
  width: 100%;
`;

const ContentLayout = styled(AntContent)`
  flex-shrink: 1;
`;

const Content = styled(AntContent)`
  height: 100%;
  background: #fff;

  ${props =>
    props.$fullWidth
      ? `
    display: block;
    overflow: auto;
  `
      : `
    display: flex;
    flex-direction: row;
    overflow: hidden;
  `}
`;

const Breadcrumb = styled(AntBreadcrumb)`
  ol {
    flex-wrap: nowrap;
  }
  & li {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }

  // remove hover state for menu links.
  && .ant-dropdown-menu-item a,
  && .ant-dropdown-menu-item a:hover {
    display: block;
    padding: 0;
    border-radius: 0;
    background: transparent;
  }
`;

const BreadcrumbLinkLabel = styled.span`
  cursor: pointer;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.85);
`;

const Logo = styled.img`
  height: 26px;
  width: auto;
  display: block;
  cursor: pointer;
`;

const WhatsNewWidget = styled(AnnounceKit)`
  padding: 0 4px;
  font-size: 16px;
  font-weight: 600;
`;

const ORGANIZATION_LIST_ROUTE = '/organization';
const SETTINGS_ROUTE = '/settings/profile';
const MANAGEMOBI_ROUTE = '/managemobi';

const UserSettings = ({ displayName, isStaff }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items } = useUploadingDrawer();
  const queryClient = useQueryClient();

  const onLogout = async () => {
    async function handleLogout() {
      try {
        await logout();
        dispatch(setUser(null));
        navigate('/', { replace: true });
      } catch {
        // do nothing
      } finally {
        queryClient.clear();
      }
    }

    if (items.every(next => [STATUS_COMPLETED, STATUS_FAILED].includes(next.status))) {
      handleLogout();
      return;
    }

    Modal.confirm({
      title: t`Are you sure to log out?`,
      icon: <ExclamationCircleOutlined />,
      content: t`Changes you made will not be saved.`,
      onOk: handleLogout,
    });
  };
  const userMenuItems = [
    {
      key: SETTINGS_ROUTE,
      label: <Link to={SETTINGS_ROUTE}>{t`Settings`}</Link>,
    },
    isStaff && {
      key: MANAGEMOBI_ROUTE,
      label: <Link to={MANAGEMOBI_ROUTE}>{t`ManageMobi`}</Link>,
    },
    {
      type: 'divider',
      style: { marginTop: 0, marginBottom: 0 },
    },
    {
      key: 'logout',
      onClick: onLogout,
      icon: <LogoutOutlined />,
      label: t`Log out`,
    },
  ].filter(item => !!item);

  return (
    <Dropdown trigger={['click']} menu={{ items: userMenuItems }}>
      <Button type="text">
        {displayName} <DownOutlined />
      </Button>
    </Dropdown>
  );
};

function Layout({ children }) {
  const [isSideNavExpanded, setSideNavExpanded] = useState(true);
  const { eventId, organizationId } = useParams();
  const navigate = useNavigate();

  const organizationName = useSelector(getName);
  const eventName = useSelector(getEventName);
  const isStaff = useSelector(getIsStaff);
  const { id: userId, firstName, lastName, email } = useSelector(getUser);
  const displayName = firstName || lastName ? [firstName, lastName].join(' ') : email;

  const { loading } = useOrganization(organizationId);
  const { tabs: organizationTabs, isLoading: isLoadingTabs } = useOrganizationTabs(organizationId);

  const makeUrl = target =>
    generatePath(`/organization/:organizationId/${target}`, { organizationId });

  if (loading || isLoadingTabs) {
    return <Loading />;
  }

  return (
    <PageLayout
      // Setting a CSS variable so anything within this tree has dynamic access
      style={{
        [SIDE_NAV_PROPERTY]: isSideNavExpanded ? SIDE_NAV_WIDTH.Expanded : SIDE_NAV_WIDTH.Collapsed,
      }}
    >
      <Header>
        <PageHeader
          extra={
            <Space size="small">
              <WhatsNewWidget
                lang="auto"
                widget={`https://updates.eventmobi.com/widgets/v2/${ANNOUNCEKIT_WHATSNEW_WIDGET_ID}`}
                user={{
                  id: userId,
                  name: displayName,
                  email,
                }}
                widgetStyle={{
                  margin: '0 0 4px 4px',
                  fontSize: '16px',
                }}
              >
                {t`What's New?`}
              </WhatsNewWidget>
              <HelpDropdown firstName={firstName} lastName={lastName} email={email} />
              <UserSettings displayName={displayName} isStaff={isStaff} />
            </Space>
          }
          title={
            <Row align="middle">
              {!!eventId && (
                <Button
                  style={{ border: 'none', marginInline: 8 }}
                  onClick={() => setSideNavExpanded(prev => !prev)}
                  icon={<MenuOutlined />}
                />
              )}
              <Breadcrumb
                separator={<img src={SeparatorImage} alt="separator" />}
                items={[
                  {
                    title: (
                      <Logo src={LogoImage} onClick={() => navigate(ORGANIZATION_LIST_ROUTE)} />
                    ),
                  },
                  ...(organizationId
                    ? [
                        {
                          title: <BreadcrumbLinkLabel>{organizationName}</BreadcrumbLinkLabel>,
                          menu: {
                            items: [
                              ...organizationTabs.map(tab => ({
                                key: tab.key,
                                label: (
                                  <Link
                                    to={makeUrl(tab.key)}
                                    style={tab.color ? { color: tab.color } : undefined}
                                  >
                                    {tab.label}
                                  </Link>
                                ),
                              })),
                              {
                                type: 'divider',
                                style: { marginTop: 0, marginBottom: 0 },
                              },
                              {
                                key: ORGANIZATION_LIST_ROUTE,
                                label: (
                                  <Link
                                    to={ORGANIZATION_LIST_ROUTE}
                                  >{t`Switch Organizations`}</Link>
                                ),
                              },
                            ],
                          },
                          dropdownProps: { trigger: ['click'] },
                        },
                      ]
                    : []),
                  ...(eventId ? [{ title: eventName }] : []),
                ]}
              />
              {eventId && <PreviewButton />}
            </Row>
          }
        />
        <AppAlerts />
      </Header>
      <ContentLayout>
        <Content $fullWidth={!window.location.pathname.includes('/event/')}>
          <SideNavExpandedContext.Provider value={isSideNavExpanded}>
            {children}
          </SideNavExpandedContext.Provider>
        </Content>
        <UploadingDrawer />
      </ContentLayout>
      <AnnounceKit
        lang="auto"
        widget={`https://updates.eventmobi.com/widgets/v2/${ANNOUNCEKIT_SUGGEST_FEATURE_WIDGET_ID}`}
      />
    </PageLayout>
  );
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
};

PageHeader.propTypes = {
  title: PropTypes.node,
  extra: PropTypes.node,
};

UserSettings.propTypes = {
  displayName: PropTypes.string,
  isStaff: PropTypes.bool,
};

export default Layout;
